import * as React from 'react'

import { useApolloClient } from '@apollo/client'
import {
  AddMembersToPostsGroupDocument,
  CreateDynamicTeamDocument,
  GroupMembersSearchDocument,
  LinkDynamicTeamToGroupDocument,
  RemoveMembersFromPostsGroupDocument,
  UpdateDynamicTeamSpecificationDocument,
} from 'generated/resources/graphql'

import { useCurrentSession } from 'modules/core/hooks/useCurrentSession'
import { useGraphqlDataCollectionSource } from 'modules/core/hooks/useGraphqlDataCollectionSource'
import { useMutation } from 'modules/core/hooks/useMutation'
import { handleResult } from 'modules/core/lib/handleMutation'
import i18n from 'modules/core/lib/i18n'
import app from 'modules/core/store/models/app'

import { isSpecificationEmpty } from 'modules/posts/components/DynamicTeams/SpecificationSelector'
import type { EmployeeSelectorV2MultiSelectState } from 'modules/employees/components/EmployeeSelectorV2/types'

import type { CommunityMemberEmployeeFragment } from 'generated/resources/graphql'
import { Delete, Plus, Sliders } from '@factorialco/f0-react/icons/app'

type GroupEmployee = CommunityMemberEmployeeFragment
type View = 'members' | 'invite' | 'automatic-invites'

type SecondaryAction = {
  label: string
  icon: typeof Plus | typeof Sliders | typeof Delete
  onClick: () => void
}

type UseMembersListModalParams = {
  groupId: number
  groupTitle: string
  canManageMembers?: boolean
  isDynamicCommunitiesEnabled?: boolean
  dynamicTeamId?: number | null
}

export const useMembersListModal = ({
  groupId,
  groupTitle,
  canManageMembers = false,
  isDynamicCommunitiesEnabled = false,
  dynamicTeamId = null,
}: UseMembersListModalParams) => {
  const { employee: currentEmployee } = useCurrentSession()
  const currentEmployeeId = currentEmployee?.id
  const client = useApolloClient()

  // View state
  const [view, setView] = React.useState<View>('members')

  // Members state
  const [isRemoving, setIsRemoving] = React.useState(false)
  const [isInvitingMembers, setIsInvitingMembers] = React.useState(false)
  const [employeeSelection, setEmployeeSelection] =
    React.useState<EmployeeSelectorV2MultiSelectState>({ type: 'manual', ids: [] })

  // Automatic invites state
  const [loadedSpecification, setLoadedSpecification] = React.useState<string | null>(null)
  const [currentSpecification, setCurrentSpecification] = React.useState<string | null>(null)
  const [isSubmittingAutoInvites, setIsSubmittingAutoInvites] = React.useState(false)
  const [autoInvitesError, setAutoInvitesError] = React.useState<string | null>(null)
  const [hasEvaluationError, setHasEvaluationError] = React.useState(false)

  // Calculate hasChanges based on comparing specifications
  const hasAutoInvitesChanges = React.useMemo(() => {
    if (!currentSpecification) return false
    if (isSpecificationEmpty(currentSpecification)) return false
    return currentSpecification !== loadedSpecification
  }, [currentSpecification, loadedSpecification])

  // GraphQL Mutations
  const [removeMembers] = useMutation(RemoveMembersFromPostsGroupDocument, {
    extractorFn: (mutationData) => mutationData.postsMutations.removeMembersPostsGroup,
  })

  const [addMembers] = useMutation(AddMembersToPostsGroupDocument, {
    extractorFn: (mutationData) => mutationData.postsMutations.addMembersPostsGroup,
  })

  const [createDynamicTeam] = useMutation(CreateDynamicTeamDocument, {
    extractorFn: (data) => data.postsMutations.createPostsDynamicTeam,
  })

  const [updateDynamicTeam] = useMutation(UpdateDynamicTeamSpecificationDocument, {
    extractorFn: (data) => data.postsMutations.updatePostsDynamicTeam,
  })

  const [linkDynamicTeamToGroup] = useMutation(LinkDynamicTeamToGroupDocument, {
    extractorFn: (data) => data.postsMutations.updatePostsGroup,
  })

  // Handlers
  const handleRemoveMembers = React.useCallback(
    async (memberIds: number[]) => {
      if (memberIds.length === 0) return

      setIsRemoving(true)

      const result = await removeMembers({
        variables: {
          id: groupId,
          employeeIds: memberIds,
        },
      })

      const handled = handleResult(result, () => {
        app.setFlash({
          type: 'wrong',
          message: i18n.t('posts.communities.members_removed_error'),
        })
      })

      if (handled.success) {
        app.setFlash({
          type: 'correct',
          message: i18n.t('posts.communities.members_removed_success'),
        })
      }

      setIsRemoving(false)
    },
    [groupId, removeMembers]
  )

  const handleBackToMembers = React.useCallback(() => {
    setView('members')
    setEmployeeSelection({ type: 'manual', ids: [] })
    setLoadedSpecification(null)
    setCurrentSpecification(null)
    setAutoInvitesError(null)
    setHasEvaluationError(false)
  }, [])

  const handleInviteEmployeeChange = React.useCallback(
    (state: EmployeeSelectorV2MultiSelectState) => {
      setEmployeeSelection(state)
    },
    []
  )

  const handleInviteMembersSubmit = React.useCallback(async () => {
    // Check if selection is valid (don't allow empty manual selection)
    if (employeeSelection.type === 'manual' && employeeSelection.ids.length === 0) return

    setIsInvitingMembers(true)

    const result = await addMembers({
      variables: {
        id: groupId,
        employeeIds: [], // For backward compatibility
        employeeSelection: employeeSelection
      }
    })

    const handled = handleResult(result, () => {
      app.setFlash({
        type: 'wrong',
        message: i18n.t('posts.communities.members_added_error'),
      })
    })

    setIsInvitingMembers(false)

    if (handled.success) {
      app.setFlash({
        type: 'correct',
        message: i18n.t('posts.communities.members_added_success'),
      })
      handleBackToMembers()
    }
  }, [addMembers, groupId, handleBackToMembers, employeeSelection])

  const handleAutoInvitesSpecificationLoaded = React.useCallback((specification: string | null) => {
    setLoadedSpecification(specification)
    setCurrentSpecification(specification)
    setAutoInvitesError(null)
  }, [])

  const handleAutoInvitesSpecificationChange = React.useCallback((specification: string) => {
    setCurrentSpecification(specification)
  }, [])

  const handleAutoInvitesEvaluationError = React.useCallback((hasError: boolean) => {
    setHasEvaluationError(hasError)
  }, [])

  const handleAutoInvitesSubmit = React.useCallback(async () => {
    setIsSubmittingAutoInvites(true)
    setAutoInvitesError(null)

    const specJson = currentSpecification

    if (!specJson || isSpecificationEmpty(specJson)) {
      setAutoInvitesError(i18n.t('posts.communities.automatic_invites.errors.empty_specification'))
      setIsSubmittingAutoInvites(false)
      return
    }

    if (dynamicTeamId) {
      // Update existing dynamic team
      const updateResult = await updateDynamicTeam({
        variables: {
          id: dynamicTeamId,
          specification: specJson,
        },
      })

      const handledUpdateResult = handleResult(updateResult, () => {
        setAutoInvitesError(
          i18n.t('posts.communities.automatic_invites.errors.updating_dynamic_team')
        )
      })

      setIsSubmittingAutoInvites(false)

      if (handledUpdateResult.success) {
        app.setFlash({
          type: 'correct',
          message: i18n.t('posts.communities.automatic_invites.success'),
        })
        handleBackToMembers()
      }
    } else {
      // Create new dynamic team
      const dynamicTeamName = `${groupTitle} - ${i18n.t(
        'posts.dynamic_team_audience.dynamic_community'
      )}`
      const createResult = await createDynamicTeam({
        variables: {
          name: dynamicTeamName,
          specification: specJson,
        },
      })

      const handledCreateResult = handleResult(createResult, () => {
        setAutoInvitesError(
          i18n.t('posts.communities.automatic_invites.errors.creating_dynamic_team')
        )
      })

      if (!handledCreateResult.success) {
        setIsSubmittingAutoInvites(false)
        return
      }

      const createData = handledCreateResult.data
      const newDynamicTeamId = createData?.dynamicTeam?.id
      if (!newDynamicTeamId) {
        setAutoInvitesError(
          i18n.t('posts.communities.automatic_invites.errors.creating_dynamic_team')
        )
        setIsSubmittingAutoInvites(false)
        return
      }

      // Link the new dynamic team to the group
      const linkResult = await linkDynamicTeamToGroup({
        variables: {
          id: groupId,
          dynamicTeamId: newDynamicTeamId,
        },
      })

      const handledLinkResult = handleResult(linkResult, () => {
        setAutoInvitesError(
          i18n.t('posts.communities.automatic_invites.errors.linking_dynamic_team_to_group')
        )
      })

      setIsSubmittingAutoInvites(false)

      if (handledLinkResult.success && handledLinkResult?.data?.group?.id) {
        app.setFlash({
          type: 'correct',
          message: i18n.t('posts.communities.automatic_invites.success'),
        })
        handleBackToMembers()
      }
    }
  }, [
    currentSpecification,
    dynamicTeamId,
    updateDynamicTeam,
    createDynamicTeam,
    linkDynamicTeamToGroup,
    groupId,
    groupTitle,
    handleBackToMembers,
  ])

  // Secondary actions for the data collection
  const secondaryActions: SecondaryAction[] | undefined = React.useMemo(() => {
    if (!canManageMembers) return undefined

    const actions: SecondaryAction[] = [
      {
        label: i18n.t('posts.communities.invite_people'),
        icon: Plus,
        onClick: () => setView('invite'),
      },
    ]

    if (isDynamicCommunitiesEnabled) {
      actions.unshift({
        label: i18n.t('posts.communities.set_automatic_invites'),
        icon: Sliders,
        onClick: () => setView('automatic-invites'),
      })
    }

    return actions
  }, [canManageMembers, isDynamicCommunitiesEnabled])

  const itemActions = React.useCallback(
    (item: GroupEmployee) => {
      if (!canManageMembers || item.id === currentEmployeeId) return []

      return [
        {
          label: i18n.t('posts.communities.remove_member'),
          icon: Delete,
          onClick: () => handleRemoveMembers([item.id]),
          disabled: isRemoving,
        },
      ]
    },
    [canManageMembers, currentEmployeeId, handleRemoveMembers, isRemoving]
  )

  const handleBulkAction = React.useCallback(
    async (action: string, items: { id: number }[]) => {
      if (action === 'remove') {
        const memberIds = items.map((item) => item.id)
        handleRemoveMembers(memberIds)
      }
    },
    [handleRemoveMembers]
  )

  // Data source for OneDataCollection
  const dataSource = useGraphqlDataCollectionSource(
    {
      document: GroupMembersSearchDocument,
      mapData: (data) =>
        data.posts?.group?.employeesConnection?.edges
          ?.map((edge) => edge?.node)
          .filter((node): node is GroupEmployee => node !== null) ?? [],
      mapVariables: ({ search }) => ({
        id: groupId,
        fuzzySearch: search || undefined,
      }),
      mapPagination: (data) => ({
        total: data.posts?.group?.employeesConnection?.totalCount ?? 0,
        cursor: data.posts?.group?.employeesConnection?.pageInfo?.endCursor ?? undefined,
        hasNextPage: data.posts?.group?.employeesConnection?.pageInfo?.hasNextPage ?? false,
      }),
      paginationType: 'infinite-scroll',
      search: {
        enabled: true,
      },
      secondaryActions: secondaryActions
        ? {
            expanded: 2 satisfies 0 | 1 | 2,
            actions: () => secondaryActions,
          }
        : undefined,
      itemActions: canManageMembers ? itemActions : undefined,
      selectable: canManageMembers
        ? (item: GroupEmployee) => (item.id === currentEmployeeId ? undefined : item.id)
        : undefined,
    },
    [groupId, secondaryActions, canManageMembers, itemActions, currentEmployeeId]
  )

  // Subscription refetch function
  const refetchMembersList = React.useCallback(() => {
    client.refetchQueries({
      include: [GroupMembersSearchDocument],
    })
  }, [client])

  return {
    // View state
    view,
    setView,

    // Data source
    dataSource,
    handleBulkAction,

    // Members invitation
    employeeSelection,
    handleInviteEmployeeChange,
    handleInviteMembersSubmit,
    isInvitingMembers,

    // Automatic invites
    loadedSpecification,
    currentSpecification,
    handleAutoInvitesSpecificationLoaded,
    handleAutoInvitesSpecificationChange,
    handleAutoInvitesEvaluationError,
    handleAutoInvitesSubmit,
    isSubmittingAutoInvites,
    autoInvitesError,
    hasAutoInvitesChanges,
    hasEvaluationError,

    // Common handlers
    handleBackToMembers,
    refetchMembersList,
  }
}
